import { defineStore } from 'pinia';
import { toast } from 'vue-sonner';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationAction {
    label: string;
    /** Invoked when the user clicks the CTA. May be async. */
    handler: () => void | Promise<void>;
}

export interface NotifyOptions {
    type?: NotificationType;
    title: string;
    message?: string;
    action?: NotificationAction;
    /**
     * When set, a toast with the same key is reused: re-raising the same key
     * updates the existing toast instead of stacking a duplicate. Also lets
     * `dismissByKey` find and remove it later.
     */
    dedupeKey?: string;
    /** Auto-dismiss after this many ms. `null` keeps it until dismissed. */
    durationMs?: number | null;
}

const DEFAULT_DURATIONS: Record<NotificationType, number | null> = {
    success: 4000,
    info: 5000,
    warning: null,
    error: null
};

/**
 * Thin wrapper around vue-sonner's `toast` API. Centralises our default
 * durations and the deduped-by-key behaviour the auth flow relies on, while
 * the actual rendering is handled by the shadcn-vue Sonner component mounted
 * in App.vue.
 */
export const useNotificationsStore = defineStore('NotificationsStore', {
    actions: {
        notify(options: NotifyOptions): string | number {
            const type = options.type ?? 'info';
            const durationMs =
                options.durationMs !== undefined ? options.durationMs : DEFAULT_DURATIONS[type];

            return toast[type](options.title, {
                id: options.dedupeKey,
                description: options.message,
                // sonner treats Infinity as "never auto-dismiss".
                duration: durationMs == null ? Infinity : durationMs,
                action: options.action
                    ? {
                          label: options.action.label,
                          onClick: () => {
                              void options.action!.handler();
                          }
                      }
                    : undefined
            });
        },
        success(title: string, message?: string) {
            return this.notify({ type: 'success', title, message });
        },
        error(title: string, message?: string) {
            return this.notify({ type: 'error', title, message });
        },
        warning(title: string, message?: string) {
            return this.notify({ type: 'warning', title, message });
        },
        info(title: string, message?: string) {
            return this.notify({ type: 'info', title, message });
        },
        dismissByKey(dedupeKey: string) {
            toast.dismiss(dedupeKey);
        }
    }
});
